import { ArrowBack, ArrowRight, Close } from "@mui/icons-material";
import { AppBar, Box, IconButton, List, ListItem, ListItemText, Toolbar, Typography } from "@mui/material";
import type { FC, JSX } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthProvider";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useChosenDepartment } from "../../../contexts/ChosenDepartment";
import type { TranslationKeys } from "../../../i18n/types";
import { toKebabCase } from "../../../utils/strings";

const NOTIFICATION_TYPES = {
	actionRequired: { label: "admin.notification.settings.action-required.label", desc: "admin.notification.settings.action-required.desc" },
	dailyNotifications: { label: "admin.notification.settings.daily-notification.label", desc: "admin.notification.settings.daily-notification.desc" },
	dailyPickupSummary: { label: "admin.notification.settings.daily-pickup-summary.label", desc: "admin.notification.settings.daily-pickup-summary.desc" },
} as const satisfies {
	[type: string]: {
		label: TranslationKeys & `admin.notification.settings.${string}.label`;
		desc: TranslationKeys & `admin.notification.settings.${string}.desc`;
	};
};

export const AdminNotificationSettings: FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const {
		chosenDepartment: { department_id: departmentId },
	} = useChosenDepartment();
	const { track } = useAnalytics();
	const { userInfo } = useAuth();
	const hasPushToken = !!userInfo?.push_notification_token;

	const handleBack = () => {
		track("admin_notification_settings_click", { action: "back" });
		navigate(`/${departmentId}/settings`);
	};
	const handleClose = () => {
		track("admin_notification_settings_click", { action: "close" });
		navigate(`/${departmentId}/settings`);
	};
	const handleNotificationTypeClick = (notificationType: keyof typeof NOTIFICATION_TYPES) => {
		track("admin_notification_settings_click", { action: "notification_click", notificationType, hasPushToken });

		const path = `/${departmentId}/settings/notification-settings/${toKebabCase(notificationType)}`;
		navigate(path);
	};

	return (
		<Box data-testid="notification-settings-root" sx={{ height: "100vh", backgroundColor: "#fff", position: "relative", overflow: "hidden" }}>
			<AppBar position="static" elevation={0} sx={{ backgroundColor: "#fff", color: "#000", marginTop: "60px" }}>
				<Toolbar>
					<IconButton edge="start" onClick={handleBack} sx={{ p: 0.5 }}>
						<ArrowBack />
					</IconButton>

					<Typography variant="h6" sx={{ flexGrow: 1, textAlign: "center", fontWeight: 600, color: "inherit" }}>
						{t("notification.settings")}
					</Typography>

					<IconButton
						edge="end"
						onClick={handleClose}
						sx={{
							p: 0.5,
							"& .MuiSvgIcon-root": { fontSize: "18px" },
						}}
					>
						<Close />
					</IconButton>
				</Toolbar>
			</AppBar>
			<Box
				sx={{
					mx: 2,
					backgroundColor: "#fff",
					borderRadius: 2,
					border: "1px solid #e9ecef",
					boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
				}}
			>
				{!hasPushToken && (
					<Box sx={{ px: 3, py: 2, backgroundColor: "#f8f9fa", borderBottom: "1px solid #f0f0f0" }}>
						<Typography variant="body2" sx={{ color: "#666", fontSize: "14px", lineHeight: 1.4 }}>
							{t("download.app.push.notifications")}
						</Typography>
					</Box>
				)}

				<List sx={{ p: 0 }}>
					{Object.entries(NOTIFICATION_TYPES).map<JSX.Element>(([notificationType, config]) => {
						const label = t(config.label);
						const desc = t(config.desc);

						return (
							<ListItem
								key={notificationType}
								data-testid={`notification-settings-item-${notificationType}`}
								onClick={() => {
									handleNotificationTypeClick(notificationType as keyof typeof NOTIFICATION_TYPES);
								}}
								sx={{
									py: 2.5,
									px: 3,
									cursor: "pointer",
									"&:hover": { backgroundColor: "#f5f5f5" },
									"&:not(:last-child)": { borderBottom: "1px solid #f0f0f0" },
								}}
							>
								<ListItemText
									primary={label}
									secondary={desc}
									slotProps={{
										primary: { sx: { fontSize: "16px", fontWeight: 500 } },
										secondary: { sx: { fontSize: "14px", color: "#666" } },
									}}
								/>
								<ArrowRight sx={{ color: "#999" }} />
							</ListItem>
						);
					})}
				</List>
			</Box>
		</Box>
	);
};
